/* Purpose: Central page for all loan activity. Displays a client's list of currentLoans 
 *          or (when click on a loan) displays the details of a specific loan,
 *          or (when click Apply For New Loan) displays the form to apply for a new loan.
 * Props: ClientID (str)
 *        credentials (map)
 * QueryStringParameters: createNewLoan (boolean) --optional
 * Author: R. Markowitz (April 2025)
 * Dev Notes: 
*/

import React, {useState, useEffect} from 'react';
import PortalNavBar from '../../Layout/PortalNavBar';
import {Form, Row, Col, Container, Card, Image, Stack, ProgressBar, Badge} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LoanTemplate from './LoanTemplate';
import PageTitle from '../../Components/PageTitle';
import NewLoan from './NewLoan';
import SubmitButton from '../../Components/Button';
import Spinner from 'react-bootstrap/Spinner';
import ConfirmDelete from '../../Components/ConfirmDelete';
import { statusColors } from '../../Data/MaxData';

const Loans = (props) => {
    const queryString = window.location.search
    const deleteIcon = require("../../Images/deleteSmall.png")
    const [currentLoanList, setCurrentLoanList] = useState();
    const [clickedLoan, setClickedLoan] = useState("");
    const [clickedNewLoan, setClickedNewLoan] = useState("");
    const [dataLoaded, setDataLoaded] = useState(""); 
    const [loanFileChange, setLoanFileChange] = useState(false); // When a file with hasFromPortalOption = true is uploaded to any loan, set to true
    const ClientID = props.ClientID;
    const credentials = props.credentials;
    const [sortSelection, setSortSelection] = useState("21")
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    useEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth' });
    }, [])

    // If createNewLoan is in the queryString, call handleclickedNewLoan and remove it from the url
    useEffect(() => {
        if (queryString.includes("createNewLoan")){
            setClickedNewLoan("True");
        }
    },[queryString])


    // When the clientID is first set and whenever a loan was clicked and then the user goes back to loan list,
    // fetch a list of loanIds from dynamodb and set currentLoanList
    useEffect(() => {
        if (!clickedNewLoan){
           setDataLoaded(""); 
        }
        const getLoanList = async () => {
            try {
                const response = await fetch(`https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/getLoanList?ClientID=${encodeURIComponent(ClientID)}`);
                if (!response.ok) {
                    setCurrentLoanList([{"LoanID": "Something went wrong when tring to access your current loans"}]);
                    console.log("Error loading loan list. Response not ok.");
                }else{
                    const data = await response.json();
                    if (data && !data.length == 0) {
                            // handleSortChange sorts the data and sets currentloanlist to the sorted data
                            handleSortChange("21", data)                        
                    } else {
                        setCurrentLoanList([{"LoanID": "No Loans on File."}]);
                        console.log("Data empty.");
                    }
                }
            } catch (error) {
                setCurrentLoanList([{"LoanID": "Something went wrong when tring to access your current loans"}]);
            }
        }
        if (ClientID && !showDeleteConfirmation){
          if (loanFileChange){
            setLoanFileChange(false)
          }
          getLoanList();  
        }
        
    }, [clickedNewLoan, ClientID, loanFileChange, showDeleteConfirmation])

    // When current Loan list finished loading and updating, set DataLoaded to true
    useEffect(() => {
        setDataLoaded("True");
        if (queryString.includes("LoanID")){
            let loanID = window.decodeURIComponent(queryString.substring(queryString.indexOf("LoanID") + 7))
            console.log("LoanID",  loanID)
            setClickedLoan({"LoanID":loanID})
        }
    }, [currentLoanList])

    // When click new loan, set clickedNewLoan to true
    const handleNewLoanClick = () => {
        setClickedNewLoan("True");
    }

    const handleCompleteApplication = (loan) => {
        setClickedNewLoan("");
        setClickedLoan(loan);
    }

    // When click on a loan, set clicked loan to the loan object
    const handleLoanClick = (loan) => {
        setClickedLoan(loan);
    }

    const handleReturnToLoanList = () => {
        setClickedNewLoan("")
        setClickedLoan("")
        if (queryString){
            window.location.search = ""
        }
        if (window.location.hash){
            window.location.hash=''
        }
    }

    // When a sorting option is chosen, resort the loan list
    // must send value (how to sort), optional to send data
    const handleSortChange = (value, data) => {
        setSortSelection(value)

        if (!data){
            data = [...currentLoanList]
        }

        if (value == "AZ"){
            // Last Name A to Z
            data.sort((a, b) => {
                let bText = b.LoanID.toLowerCase() || ''
                let aText = a.LoanID.toLowerCase() || ''

                if (aText > bText){
                    return 1
                }else if (aText < bText){
                    return -1
                }else{
                    return 0
                }
            })

        }else if(value == "ZA"){
            // Last Name Z to A
            data.sort((a, b) => {
                let bText = b.LoanID.toLowerCase() || ''
                let aText = a.LoanID.toLowerCase() || ''

                if (aText > bText){
                    return -1
                }else if (aText < bText){
                    return 1
                }else{
                    return 0
                }
            })

        }else if (value == "12"){
            // Date created Old to New
            data.sort((a, b) => {
                let bText = new Date(b.DateCreated)
                let aText = new Date(a.DateCreated)

                if (aText > bText){
                    return 1
                }else if (aText < bText){
                    return -1
                }else{
                    return 0
                }
            })


        }else if(value == "21"){
            // Date created New to Old
            data.sort((a, b) => {
                let bText = new Date(b.DateCreated)
                let aText = new Date(a.DateCreated)

                if (aText > bText){
                    return -1
                }else if (aText < bText){
                    return 1
                }else{
                    return 0
                }
            })

        }
        setCurrentLoanList([...data])

    }

    // When Delete loan is clicked, confirm that the client really intended to delete the loan.
    // If confirmed, delete the loan from db
    const handleDeleteLoan = (loan) => {
        setShowDeleteConfirmation(loan.LoanID);
    }

    return(
        <div>
        <PortalNavBar ClientID = {ClientID}/>
        <div>
            {clickedLoan &&(
                <Container>
                    <LoanTemplate 
                        key = {clickedLoan} 
                        LoanID = {clickedLoan.LoanID} 
                        ClientID = {ClientID}
                        loanBasicInfo = {clickedLoan}
                        LoanList = {currentLoanList}
                        handleReturnToLoanList = {handleReturnToLoanList}
                        setLoanFileChange = {setLoanFileChange}
                        credentials = {credentials}

                    />
                    <SubmitButton onClick = {handleReturnToLoanList} text = "Return to List of Current Loans" width="50"/> 
                </Container>
                
            )}
        </div>
        
        <div>
          {!clickedLoan && !clickedNewLoan &&(
            <PageTitle pageName="Current Loans" />
          )}
        </div>

        <Container fluid>
            <Container className='m-5'>
                {!clickedLoan && !clickedNewLoan &&(
                    <SubmitButton onClick= {handleNewLoanClick} text = "Apply for New Loan" />
                )}
                
            </Container>
            {/* If current Loan List is loaded */}
            
                {!clickedLoan && !clickedNewLoan && currentLoanList &&(
                <>
                    <Row className='mx-5'>
                        <Col xs='auto'>
                            <Form.Label>Sort: </Form.Label>
                        </Col>
                        <Col xs = 'auto'>
                            <Form.Select aria-label="sort-options" value = {sortSelection} onChange = {(event) => handleSortChange(event.currentTarget.value)}>
                                <option value="AZ" >Last Name - A to Z</option>
                                <option value="ZA">Last Name - Z to A</option>
                                <option value="12">Date Created - Old to New</option>
                                <option value="21">Date Created - New to Old</option>
                            </Form.Select>
                        </Col>
                    </Row>
                    <Row xs={1} md={3} className="g-4 m-5">
                        {currentLoanList.map((loan, index) => (
                            <Col key = {index}>
                            {loan.LoanID === "No Loans on File."?(
                                <Card key = {index}>
                                    <Card.Title><strong>{loan.LoanID}</strong></Card.Title>
                                </Card>
                            ):(
                                <Card key = {index} className = "p-2 content-align-center border-10" border = {statusColors[loan.Status]}>
                                        <Card.Title>
                                            <Stack direction = "horizontal" gap ={2}>
                                                 <>{loan.LoanID}</>
                                                 <a className = "ms-auto" onClick = {() => {handleDeleteLoan(loan)}}><Image alt = "Delete" src= {deleteIcon} fluid/></a>
                                            </Stack>
                                        </Card.Title>
                                        <p>Type: {loan.LoanType}</p>
                                        <Badge pill bg={statusColors[loan.Status]} >{loan.Status}</Badge>
                                        
                                        
                                        <div className='content-align-center'>
                                            <SubmitButton text = "View Loan" onClick = {() => handleLoanClick(loan)} width = "90" />
                                        </div>
                                </Card>
                            )}  

                            </Col>


                    ))}

                        {showDeleteConfirmation &&(
                            <ConfirmDelete
                                ClientID = {ClientID}
                                LoanID = {showDeleteConfirmation}
                                credentials = {credentials}
                                onHide = {() => {setShowDeleteConfirmation(false)}}
                            />
                        )}
                    </Row>

                    <Row  xs = {2} sm={2} md = {2} className='g-4 m-3'>
                        <Col className='text-start'>
                            <Link className="mt-auto" to = "/Portal/Financial"><SubmitButton text = "<< Back" width = "52"/></Link>
                        </Col>
                        <Col className='text-end'>
                            <Link to = "/Portal/Review" className="mt-auto"><SubmitButton text = "Next >>" width = "52"/></Link>
                        </Col>
                    </Row>
                </>
                )}

                


            {/* If currentLoanList is not loaded */}
                {!clickedLoan && !clickedNewLoan && !currentLoanList &&(
                    <Row className='mx-3'>
                        <Col>
                        <Form.Label><strong>Loading...</strong></Form.Label>
                        </Col>
                    </Row>
                )}
                {(!dataLoaded) &&(
                    <Row className='m-2'>
                        <Col sm={1}>
                            <Spinner 
                                animation="border" 
                                role="status"
                                aria-hidden='true'
                            />
                        </Col>    
                        <Col>
                            <p className = "float-start"><strong>Loading...</strong></p>
                        </Col>
                    
                    
                    </Row>

                )}
 
        </Container>

        <Container>
            {clickedNewLoan && (
                <Container>
             
                    <NewLoan 
                        ClientID = {ClientID} 
                        onCompleteApplication = {handleCompleteApplication} 
                        credentials = {credentials}
                    />

                    <SubmitButton onClick = {handleReturnToLoanList} text = "Return to List of Current Loans"  width = "50"/>
                </Container>
            )}
        </Container>
            
    </div>
        
    );
};

export default Loans;